import { Button, cn, Spinner } from "@heroui/react";
import { usePdf } from "@mikecousins/react-pdf";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
/**
 * The legacy build of pdf.js polyfills what older browsers lack, such as
 * Map.prototype.getOrInsertComputed, which Samsung Internet 30 does not know.
 * Bundled here instead of the default from cdnjs, which is the modern build.
 */
import pdfWorkerSrc from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";
import { type FC, useMemo, useRef, useState } from "react";
import { useResumeBuilder } from "../../context";

const a4Scale = Math.SQRT1_2;
const a4HeightPt = 841.89;
const footerHeight = 56;

export const ResumePDFViewer: FC<{
    file: string;
    isSticky?: boolean;
    useWrapperHeight?: boolean;
    withNav?: boolean;
    withFooter?: boolean;
    onClose?: () => void;
}> = ({ file, isSticky, useWrapperHeight, withNav, withFooter, onClose }) => {
    const [page, setPage] = useState(1);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef(null);
    const { design } = useResumeBuilder();

    const wrapperHeight = wrapperRef?.current?.getBoundingClientRect().height;
    const wrapperWidth = wrapperRef?.current?.getBoundingClientRect().width;
    const canvasHeight = useMemo(() => {
        const height = withFooter
            ? wrapperHeight - footerHeight
            : wrapperHeight;
        const width = height * a4Scale;

        const raw =
            wrapperWidth && wrapperWidth < width
                ? wrapperWidth / a4Scale
                : height;

        return raw ? Math.floor(raw) : raw;
    }, [withFooter, wrapperHeight, wrapperWidth]);

    const pdfScale =
        useWrapperHeight && canvasHeight ? canvasHeight / a4HeightPt : 1;

    const { pdfDocument } = usePdf({
        file,
        page,
        canvasRef,
        scale: pdfScale,
        workerSrc: pdfWorkerSrc,
    });

    return (
        <div
            className={cn("flex h-full flex-col items-center")}
            ref={wrapperRef}
        >
            {!pdfDocument && (
                <div
                    className={cn(
                        "flex h-full w-full items-center justify-center",
                        useWrapperHeight && "absolute top-0 left-0",
                    )}
                >
                    <Spinner className="mt-2" />
                </div>
            )}
            {withNav && pdfDocument?.numPages > 1 && (
                <nav
                    className={cn(
                        "flex w-full items-center justify-between py-4",
                        isSticky && "sticky top-0 bg-default-50 px-4",
                    )}
                >
                    <Button
                        isDisabled={page === 1}
                        onPress={() => setPage(page - 1)}
                        isIconOnly={true}
                        style={
                            page === 1
                                ? undefined
                                : {
                                      backgroundColor:
                                          design.resumePrimaryColor,
                                      color: design.resumePrimaryContrastColor,
                                  }
                        }
                    >
                        <IconChevronLeft stroke={1.5} />
                    </Button>
                    <span className="font-semibold">
                        Seite {page} von {pdfDocument.numPages}
                    </span>
                    <Button
                        isDisabled={page === pdfDocument.numPages}
                        onPress={() => setPage(page + 1)}
                        isIconOnly={true}
                        style={
                            page === pdfDocument.numPages
                                ? undefined
                                : {
                                      backgroundColor:
                                          design.resumePrimaryColor,
                                      color: design.resumePrimaryContrastColor,
                                  }
                        }
                    >
                        <IconChevronRight stroke={1.5} />
                    </Button>
                </nav>
            )}
            {useWrapperHeight && canvasHeight ? (
                <canvas
                    ref={canvasRef}
                    style={{
                        height: `${canvasHeight}px`,
                        width: `${canvasHeight * a4Scale}px`,
                        opacity: pdfDocument ? 1 : 0,
                    }}
                    height={canvasHeight}
                    width={canvasHeight * a4Scale}
                />
            ) : null}
            {!useWrapperHeight && (
                <canvas
                    ref={canvasRef}
                    style={{
                        width: "100%",
                        maxWidth: `${Math.floor(a4HeightPt * a4Scale)}px`,
                        aspectRatio: `${a4HeightPt * a4Scale} / ${a4HeightPt}`,
                    }}
                    className={cn(!pdfDocument && "invisible")}
                />
            )}
            {withFooter && (
                <div
                    className={cn(
                        `h-[${footerHeight}px] mt-auto flex w-full items-center justify-center px-4 py-2`,
                        pdfDocument?.numPages > 1 && "justify-between",
                    )}
                >
                    {pdfDocument?.numPages > 1 && (
                        <Button
                            isDisabled={page === 1}
                            onPress={() => setPage(page - 1)}
                            isIconOnly={true}
                            style={
                                page === 1
                                    ? undefined
                                    : {
                                          backgroundColor:
                                              design.resumePrimaryColor,
                                          color: design.resumePrimaryContrastColor,
                                      }
                            }
                        >
                            <IconChevronLeft stroke={1.5} />
                        </Button>
                    )}
                    <Button
                        variant="bordered"
                        onPress={onClose}
                        className="min-w-[120px]"
                    >
                        Schließen
                    </Button>
                    {pdfDocument?.numPages > 1 && (
                        <Button
                            isDisabled={page === pdfDocument.numPages}
                            onPress={() => setPage(page + 1)}
                            isIconOnly={true}
                            style={
                                page === pdfDocument.numPages
                                    ? undefined
                                    : {
                                          backgroundColor:
                                              design.resumePrimaryColor,
                                          color: design.resumePrimaryContrastColor,
                                      }
                            }
                        >
                            <IconChevronRight stroke={1.5} />
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};
